/**
 * Two manager names, same credentials (Bobbie, 2026-08-14 evening,
 * DECISIONS.md "Two manager names, same credentials, and no bare Manager
 * title": "So two names same credentials. And remove manger as a single
 * title."). Both flavors are role 'manager' with the identical union powers
 * from #615; this module is DISPLAY ONLY - nothing here may ever decide a
 * capability, and no capability check may ever import it.
 *
 * Import-free on purpose, same reasoning as familyGroups.ts: this is
 * exercised directly by `npm run test:handlers` under
 * --experimental-strip-types, which cannot resolve users.ts's Firebase
 * imports. users.ts wraps these with the real UserRole/UserDoc types.
 */

export type ManagerTitle = 'admin' | 'company';
export const MANAGER_TITLES: readonly ManagerTitle[] = ['admin', 'company'];
export const MANAGER_TITLE_LABELS: Record<ManagerTitle, string> = {
  admin: 'Manager/Admin',
  company: 'Manager/Executive Teacher',
};

export function isManagerTitle(value: unknown): value is ManagerTitle {
  return value === 'admin' || value === 'company';
}

/**
 * The flavor label for this role/managerTitle pair, or null when the role
 * is not 'manager' or has no flavor stored yet (an existing plain account,
 * per the ruling above - the caller falls back to the plain role label).
 */
export function managerFlavorLabel(role: string, managerTitle: unknown): string | null {
  if (role === 'manager' && isManagerTitle(managerTitle)) {
    return MANAGER_TITLE_LABELS[managerTitle];
  }
  return null;
}

export interface AssignableRoleOptionLike<TRole extends string> {
  value: string;
  label: string;
  role: TRole;
  managerTitle: ManagerTitle | null;
}

/**
 * Expand the manager role into its two named flavor options within a role
 * list, so an assignment surface (invite, approve-as, role change) can
 * offer them instead of a bare Manager. Every other role passes through as
 * a single option using roleLabels for its text.
 */
export function expandManagerRoleOptions<TRole extends string>(
  roles: readonly TRole[],
  roleLabels: Record<TRole, string>,
  managerRoleValue: TRole
): AssignableRoleOptionLike<TRole>[] {
  return roles.flatMap((role): AssignableRoleOptionLike<TRole>[] => {
    if (role === managerRoleValue) {
      return MANAGER_TITLES.map((title) => ({
        value: `manager:${title}`,
        label: MANAGER_TITLE_LABELS[title],
        role,
        managerTitle: title,
      }));
    }
    return [{ value: role, label: roleLabels[role], role, managerTitle: null }];
  });
}
